import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Divider,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Box,
  Paper,
  Chip,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Event as EventIcon,
  Edit as EditIcon,
  LocationOn,
  AttachMoney,
  People,
  Link as LinkIcon,
  Close,
  Image as ImageIcon,
  Add,
  Delete,
  Schedule,
  InfoOutlined,
  Videocam
} from '@mui/icons-material';
import { format, parseISO, isValid, isAfter } from 'date-fns';
import CustomTextArea from '../../common/CustomTextArea';
import { ALLOW_PAID_EVENTS } from '../../../config/featureFlags';
import { EVENT_CATEGORIES, LOCATION_TYPES, CATEGORY_DISPLAY_NAMES } from '../../../constants/eventConstants';
import { COUNTRIES } from '../../../constants/constants';

const EditEventDetails = ({ event, onSave, isEditable }) => {

  const [selectedImage, setSelectedImage] = useState();
  const [eventData, setEventData] = useState({
    title: '',
    description: '',
    category: '',
    locationType: LOCATION_TYPES.PHYSICAL,
    // Physical location fields
    locationBuilding: '',
    locationStreet: '',
    locationSuite: '',
    locationCity: '',
    locationState: '',
    locationCountry: '',
    locationZipcode: '',
    // Online location fields
    zoomMeetingId: '',
    zoomMeetingPassword: '',
    price: '',
    isFree: false,
    isFeatured: false,
    website: '',
    maxAttendees: '',
    image: null,
    tags: []
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [schedules, setSchedules] = useState([]);
  const [deletedScheduleIds, setDeletedScheduleIds] = useState([]);

  useEffect(() => {
    if (event) {
      setEventData({
        title: event.title || '',
        description: event.description || '',
        category: event.category || '',
        locationType: event.locationType || LOCATION_TYPES.PHYSICAL,
        // Physical location fields
        locationBuilding: event.fullLocationAddress?.building || '',
        locationStreet: event.fullLocationAddress?.street || '',
        locationSuite: event.fullLocationAddress?.suite || '',
        locationCity: event.fullLocationAddress?.city || '',
        locationState: event.fullLocationAddress?.state || '',
        locationCountry: event.fullLocationAddress?.country || '',
        locationZipcode: event.fullLocationAddress?.zipcode || '',
        // Online location fields
        zoomMeetingId: event.zoomMeetingId || '',
        zoomMeetingPassword: event.zoomMeetingPassword || '',
        price: (!ALLOW_PAID_EVENTS ? '' : (event.price || '')),
        isFree: (!ALLOW_PAID_EVENTS ? true : (event.isFree || false)),
        isFeatured: event.isFeatured || false,
        website: event.website || '',
        maxAttendees: event.maxAttendees || '',
        image: event.image || null,
        tags: event.tags || []
      });

      // Set schedules from event
      if (event.schedules) {
        setSchedules(event.schedules);
      }

      // Reset deleted schedule IDs
      setDeletedScheduleIds([]);

      // Set image preview if event has an image URL
      if (event.image && typeof event.image === 'string') {
        setImagePreview(event.image);
      }
    }
  }, [event]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEventData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    if (!ALLOW_PAID_EVENTS && name === 'isFree') return; // prevent toggling when paid events disabled
    setEventData(prev => ({ ...prev, [name]: checked }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create a preview URL for the uploaded file
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      setEventData(prev => ({ ...prev, image: file }));
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setEventData(prev => ({ ...prev, image: null }));
  };

  const handleTagAdd = () => {
    if (tagInput && !eventData.tags.includes(tagInput)) {
      setEventData(prev => ({ ...prev, tags: [...prev.tags, tagInput] }));
      setTagInput('');
    }
  };

  const handleTagRemove = (tagToRemove) => {
    setEventData(prev => ({ ...prev, tags: prev.tags.filter(tag => tag !== tagToRemove) }));
  };

  const handleLocationTypeChange = (e) => {
    const { value } = e.target;
    setEventData(prev => ({
      ...prev,
      locationType: value,
      // Clear opposite location type fields when switching
      ...(value === LOCATION_TYPES.REMOTE ? {
        locationBuilding: '',
        locationStreet: '',
        locationSuite: '',
        locationCity: '',
        locationState: '',
        locationCountry: '',
        locationZipcode: ''
      } : {
        zoomMeetingId: '',
        zoomMeetingPassword: ''
      })
    }));
  };

  const handleDescriptionChange = (newValue) => {
    setEventData(prev => ({
      ...prev,
      description: newValue
    }));
  };

  const handleDeleteSchedule = (scheduleId) => {
    // Remove the schedule from the list immediately
    setSchedules(prev => prev.filter(schedule => schedule.id !== scheduleId));

    // Add the schedule ID to the deleted list
    setDeletedScheduleIds(prev => [...prev, scheduleId]);
  };

  const isFutureSchedule = (schedule) => {
    try {
      const scheduleDateTime = parseISO(`${schedule.date}T${schedule.endTime || schedule.startTime}`);
      return isAfter(scheduleDateTime, new Date());
    } catch (error) {
      console.error('Error checking if schedule is future:', error);
      return false;
    }
  };

  const canDeleteSchedule = (schedule) => {
    // Schedule must be in the future AND be deletable
    return isFutureSchedule(schedule) && schedule.isDeletable !== false;
  };

  const getDeleteTooltipTitle = (schedule) => {
    if (!isFutureSchedule(schedule)) {
      return "Cannot delete past schedules";
    }
    if (schedule.isDeletable === false && schedule.deletableComment) {
      return schedule.deletableComment;
    }
    if (schedule.isDeletable === false) {
      return "Schedule cannot be deleted";
    }
    return "Delete schedule";
  };

  const formatScheduleDisplay = (schedule) => {
    try {
      const date = format(parseISO(schedule.date), 'MMM dd, yyyy');
      const time = `${schedule.startTime} - ${schedule.endTime}`;
      return `${date} • ${time}`;
    } catch (error) {
      return `${schedule.date} • ${schedule.startTime} - ${schedule.endTime}`;
    }
  };

  const handleSave = () => {
    const formData = new FormData();

    // Append basic fields
    formData.append('id', event.id);
    formData.append('title', eventData.title);
    formData.append('description', eventData.description);
    formData.append('category', eventData.category);
    formData.append('locationType', eventData.locationType);
    formData.append('price', eventData.isFree ? 0 : Number(eventData.price));
    formData.append('maxAttendees', eventData.maxAttendees ? Number(eventData.maxAttendees) : '');
    formData.append('isFree', eventData.isFree);
    formData.append('isFeatured', eventData.isFeatured);
    formData.append('tags', eventData.tags);
    formData.append('website', eventData.website);

    // Append location fields based on location type
    if (eventData.locationType === LOCATION_TYPES.PHYSICAL) {
      formData.append('locationBuilding', eventData.locationBuilding);
      formData.append('locationStreet', eventData.locationStreet);
      formData.append('locationSuite', eventData.locationSuite);
      formData.append('locationCity', eventData.locationCity);
      formData.append('locationState', eventData.locationState);
      formData.append('locationCountry', eventData.locationCountry);
      formData.append('locationZipcode', eventData.locationZipcode);
    } else {
      formData.append('zoomMeetingId', eventData.zoomMeetingId);
      formData.append('zoomMeetingPassword', eventData.zoomMeetingPassword);
    }

    // Append deleted schedule IDs as an array
    if (deletedScheduleIds.length > 0) {
      deletedScheduleIds.forEach(scheduleId => {
        formData.append('schedulesToDelete', scheduleId);
      });
    }

    // Append image if it exists and is a file
    if (selectedImage) {
      formData.append('coverImage', selectedImage);
    }

    // If onSave prop is provided, call it
    if (onSave) {
      onSave(formData);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSave();
  };

  // Separate schedules into future and past
  const futureSchedules = schedules.filter(schedule => isFutureSchedule(schedule));
  const pastSchedules = schedules.filter(schedule => !isFutureSchedule(schedule));

  return (
    <Box>
      <form onSubmit={handleSubmit}>
        {/* Basic Info Section */}
        <Typography variant="subtitle1" fontWeight="600" gutterBottom>
          Basic Info
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Event Title"
              name="title"
              value={eventData.title}
              onChange={handleInputChange}
              required
              variant="outlined"
              disabled={isEditable}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EventIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                name="category"
                value={eventData.category}
                onChange={handleInputChange}
                disabled={isEditable}
              >
                {EVENT_CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category}>
                    {CATEGORY_DISPLAY_NAMES[category] || category.replace('_', ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mb: 3 }}>
            <CustomTextArea
              required
              label='Description'
              placeholder="Enter event description..."
              onChange={handleDescriptionChange}
              value={eventData.description}
              minHeight={200}
              disabled={isEditable}
            />
          </Box>
        </Paper>

        {/* Event Schedules Section */}
        <Typography variant="subtitle1" fontWeight="600" gutterBottom>
          Event Schedules
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          {schedules.length === 0 ? (
            <Alert severity="info">
              No schedules found for this event.
            </Alert>
          ) : (
            <>
              {futureSchedules.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" color="primary" gutterBottom>
                    Upcoming Schedules
                  </Typography>
                  <List>
                    {futureSchedules.map((schedule) => {
                      const deletable = canDeleteSchedule(schedule);
                      const tooltipTitle = getDeleteTooltipTitle(schedule);

                      return (
                        <ListItem
                          key={schedule.id}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            mb: 1,
                            '&:hover': {
                              backgroundColor: 'action.hover'
                            }
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Schedule color="primary" />
                                <Typography variant="body1" fontWeight="medium">
                                  {formatScheduleDisplay(schedule)}
                                </Typography>
                                {schedule.isCurrent && (
                                  <Chip
                                    label="Current"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}
                                {!deletable && schedule.isDeletable === false && (
                                  <Tooltip title={schedule.deletableComment || "Cannot be deleted"}>
                                    <InfoOutlined color="warning" fontSize="small" />
                                  </Tooltip>
                                )}
                              </Box>
                            }
                            secondary={
                              <Typography variant="body2" color="text.secondary">
                                {schedule.tickets?.length || 0} ticket type(s) available
                                {!deletable && schedule.isDeletable === false && (
                                  <Box component="span" sx={{ ml: 1, fontStyle: 'italic' }}>
                                    • {schedule.deletableComment || "Non-deletable"}
                                  </Box>
                                )}
                              </Typography>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Tooltip title={tooltipTitle}>
                              <span>
                                <IconButton
                                  edge="end"
                                  color={deletable ? "error" : "default"}
                                  onClick={() => deletable && handleDeleteSchedule(schedule.id)}
                                  disabled={!deletable}
                                >
                                  <Delete />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )}

              {pastSchedules.length > 0 && (
                <Box>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Past Schedules
                  </Typography>
                  <List>
                    {pastSchedules.map((schedule) => (
                      <ListItem
                        key={schedule.id}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1,
                          opacity: 0.6
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Schedule color="disabled" />
                              <Typography variant="body1" color="text.secondary">
                                {formatScheduleDisplay(schedule)}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography variant="body2" color="text.secondary">
                              Completed event • Cannot be deleted
                            </Typography>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title="Cannot delete past schedules">
                            <span>
                              <IconButton
                                edge="end"
                                disabled
                              >
                                <Delete color="disabled" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </>
          )}

          {deletedScheduleIds.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {deletedScheduleIds.length} schedule(s) will be deleted when you save changes.
            </Alert>
          )}
        </Paper>

        {/* Location Section */}
        <Typography variant="subtitle1" fontWeight="600" gutterBottom>
          Location
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Location Type</InputLabel>
              <Select
                label="Location Type"
                name="locationType"
                value={eventData.locationType}
                onChange={handleLocationTypeChange}
                disabled={isEditable}
              >
                <MenuItem value={LOCATION_TYPES.PHYSICAL}>Physical Location</MenuItem>
                <MenuItem value={LOCATION_TYPES.REMOTE}>Remote/Online</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {eventData.locationType === LOCATION_TYPES.PHYSICAL ? (
            <>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Building Name"
                  name="locationBuilding"
                  value={eventData.locationBuilding}
                  onChange={handleInputChange}
                  variant="outlined"
                  disabled={isEditable}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Street Address"
                  name="locationStreet"
                  value={eventData.locationStreet}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  disabled={isEditable}
                />
                <TextField
                  fullWidth
                  label="Suite"
                  name="locationSuite"
                  value={eventData.locationSuite}
                  onChange={handleInputChange}
                  variant="outlined"
                  disabled={isEditable}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="City"
                  name="locationCity"
                  value={eventData.locationCity}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  disabled={isEditable}
                />
                <TextField
                  fullWidth
                  label="State/Province"
                  name="locationState"
                  value={eventData.locationState}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  disabled={isEditable}
                />
                <TextField
                  fullWidth
                  label="Zip/Postal Code"
                  name="locationZipcode"
                  value={eventData.locationZipcode}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  disabled={isEditable}
                />
              </Box>

              <FormControl fullWidth required>
                <InputLabel>Country</InputLabel>
                <Select
                  label="Country"
                  name="locationCountry"
                  value={eventData.locationCountry}
                  onChange={handleInputChange}
                  disabled={isEditable}
                >
                  {COUNTRIES.map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Zoom Meeting ID"
                name="zoomMeetingId"
                value={eventData.zoomMeetingId}
                onChange={handleInputChange}
                variant="outlined"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Videocam color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Zoom Meeting Password"
                name="zoomMeetingPassword"
                value={eventData.zoomMeetingPassword}
                onChange={handleInputChange}
                variant="outlined"
              />
            </Box>
          )}
        </Paper>

        {/* Website and Max Attendees */}
        <Typography variant="subtitle1" fontWeight="600" gutterBottom>
          Additional Information
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Website"
              name="website"
              value={eventData.website}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box>
            <TextField
              fullWidth
              label="Maximum Attendees"
              name="maxAttendees"
              type="number"
              value={eventData.maxAttendees}
              onChange={handleInputChange}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <People color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Paper>

        {/* Featured Event */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={eventData.isFeatured}
                onChange={handleCheckboxChange}
                name="isFeatured"
                color="primary"
              />
            }
            label="Featured Event"
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 1 }}>
            Featured events appear on the homepage with priority visibility
          </Typography>
        </Paper>

        {/* Image Upload */}
        <Typography variant="subtitle1" fontWeight="600" gutterBottom>
          Event Image
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="event-image-upload"
            type="file"
            onChange={handleImageUpload}
          />
          <label htmlFor="event-image-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<ImageIcon />}
              color="primary"
              fullWidth
              sx={{ mb: 2 }}
              disabled={isEditable}
            >
              Upload Image
            </Button>
          </label>

          {(imagePreview || eventData.image) && (
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={imagePreview || eventData.image}
                variant="rounded"
                sx={{ width: '100%', height: 200, borderRadius: 2 }}
              />
              <IconButton
                size="small"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.7)',
                  },
                }}
                onClick={handleRemoveImage}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Paper>

        {/* Tags */}
        <Typography variant="subtitle1" fontWeight="600" gutterBottom>
          Event Tags
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <TextField
            label="Add Tags"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleTagAdd();
              }
            }}
            variant="outlined"
            helperText="Press Enter or click the + button to add a tag"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleTagAdd} color="primary">
                    <Add />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2, width: '100%' }}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {eventData.tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                onDelete={() => handleTagRemove(tag)}
                sx={{
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText',
                }}
              />
            ))}
          </Box>
        </Paper>

        {/* Save Button */}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          startIcon={<EditIcon />}
          fullWidth
          sx={{ py: 1.5 }}
        >
          Save Changes
        </Button>
      </form>
    </Box>
  );
};

export default EditEventDetails;